import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Lottie from "lottie-react";
import { UIText } from "@pfs/react-ui-kit";
import { Button, Checkbox } from "@pc/ds-react";
import { useTranslation } from "src/hooks/useTranslation";
import { TEXT_TAGS } from "src/utils/enums";
import "./TransferDisclaimerModal.scss";

const INFO_ICON_SRC = "/images/info.png";
const TERMS_URL =
  "https://www.passportcard.co.il/financial-services/terms-of-use/";
const USAGE_LIMITS_URL =
  "https://www.passportcard.co.il/financial-services/limitations/";
const TRANSFER_LOADER_DELAY_MS = 4000;

export interface TransferDisclaimerModalProps {
  userName?: string;
  siteName: string;
  siteLogo?: string;
  /** Same aggregator as tab title / lock banner (IMPACT or TravelPayouts from get-affiliates-timers). */
  affiliateAggregatorName: string;
  limitAmount?: number;
  /**
   * Timer base from the clicked card's timerBaseRef entry (in ms).
   * When provided together with websiteTimerReceivedAt, overrides the aggregator timer
   * and shows the exact same countdown as the card.
   */
  websiteTimerServerMs?: number;
  /** Wall-clock timestamp (Date.now()) when websiteTimerServerMs was recorded. */
  websiteTimerReceivedAt?: number;
  /**
   * Called immediately when the user clicks confirm, in parallel with the loading animation.
   * Use for async work (e.g. API call + window.open) that should start right away
   * so it's ready by the time the animation finishes.
   */
  onConfirmStart?: () => void;
  onConfirm: () => void;
  onClose?: () => void;
}

const HIGHLIGHT_ROWS = [
  { icon: "/images/termsModal/compensation.png", key: "payWithCard" },
  {
    icon: "/images/termsModal/travel-delay.png",
    key: "timeRemaining",
    hasCountdown: true,
  },
  { icon: "/images/termsModal/global.png", key: "cardActiveForSite" },
  { icon: "/images/termsModal/web.png", key: "otherSiteSavingsHint" },
  { icon: "/images/termsModal/vat-refund.png", key: "balanceAvailable" },
] as const;

/** Format seconds as HH:MM:SS */
function timerDisplayFromSeconds(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

/** Match FlightLockCountdownBanner: "01 : 59 : 24" from "01:59:24". */
function timerDisplayWithSpaces(timerDisplay: string): string {
  return timerDisplay.split(":").join(" : ");
}

const TransferDisclaimerModal: React.FC<TransferDisclaimerModalProps> = ({
  userName,
  siteName,
  siteLogo,
  limitAmount = 50000,
  websiteTimerServerMs,
  websiteTimerReceivedAt,
  onConfirmStart,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [thirdPartyConsent, setThirdPartyConsent] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [animationData, setAnimationData] = useState<object | null>(null);
  const confirmTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Compute remaining ms from the card's wall-clock anchor (same logic as EligibleWebsites).
  // This gives exactly the same countdown as the card the user clicked.
  const hasWebsiteTimer =
    websiteTimerServerMs != null &&
    websiteTimerReceivedAt != null &&
    websiteTimerServerMs > 0;

  const computeWebsiteRemaining = useCallback((): number => {
    if (!hasWebsiteTimer) return 0;
    return Math.max(
      0,
      websiteTimerServerMs! - (Date.now() - websiteTimerReceivedAt!),
    );
  }, [hasWebsiteTimer, websiteTimerServerMs, websiteTimerReceivedAt]);

  const [websiteTimerDisplay, setWebsiteTimerDisplay] = useState<string | null>(
    () => {
      const remaining = computeWebsiteRemaining();
      return remaining > 0
        ? timerDisplayWithSpaces(timerDisplayFromSeconds(remaining / 1000))
        : null;
    },
  );

  useEffect(() => {
    if (!hasWebsiteTimer) return;

    const tick = () => {
      const remaining = computeWebsiteRemaining();
      setWebsiteTimerDisplay(
        remaining > 0
          ? timerDisplayWithSpaces(timerDisplayFromSeconds(remaining / 1000))
          : null,
      );
    };

    tick();
    const interval = setInterval(tick, 1000);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [hasWebsiteTimer, computeWebsiteRemaining]);

  const STATIC_FULL_TIMER = "02 : 00 : 00";

  const effectiveTimerDisplay = hasWebsiteTimer
    ? websiteTimerDisplay
    : STATIC_FULL_TIMER;

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const infoRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    fetch("/lottie/loading.json")
      .then((response) => response.json())
      .then((data) => setAnimationData(data))
      .catch((error) => {
        console.error("Failed to load Lottie animation:", error);
      });
  }, []);

  useEffect(() => {
    return () => {
      if (confirmTimeoutRef.current) {
        clearTimeout(confirmTimeoutRef.current);
      }
    };
  }, []);

  const handleConfirm = () => {
    setIsTransferring(true);
    onConfirmStart?.();
    confirmTimeoutRef.current = setTimeout(() => {
      onConfirm();
    }, TRANSFER_LOADER_DELAY_MS);
  };

  useEffect(() => {
    if (!tooltipOpen) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (infoRef.current && !infoRef.current.contains(event.target as Node)) {
        setTooltipOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [tooltipOpen]);

  const interpolations = {
    siteName: siteName || t("transferDisclaimer.defaultSiteName"),
    amount: limitAmount.toLocaleString("he-IL"),
  };

  return (
    <div className="transfer-disclaimer-modal">
      <div className="transfer-disclaimer-modal__header">
        <UIText
          text={t("transferDisclaimer.title")}
          tag={TEXT_TAGS.H2}
          size={32}
          weight={700}
        />
      </div>
      <div className="transfer-disclaimer-modal__subtitle">
        <UIText
          text={t("transferDisclaimer.subtitle")}
          tag={TEXT_TAGS.P}
          size={18}
          weight={400}
        />
      </div>

      <div className="transfer-disclaimer-modal__highlights">
        {HIGHLIGHT_ROWS.map((row) => (
          <div
            className="transfer-disclaimer-modal__highlight-row"
            key={row.key}
          >
            <img
              src={row.icon}
              alt=""
              className="transfer-disclaimer-modal__highlight-icon"
              aria-hidden
            />
            <div className="transfer-disclaimer-modal__highlight-content">
              {"hasCountdown" in row && row.hasCountdown ? (
                <div className="transfer-disclaimer-modal__countdown-row">
                  <UIText
                    text={t(`transferDisclaimer.${row.key}`)}
                    tag={TEXT_TAGS.SPAN}
                    size={18}
                    weight={400}
                  />
                  <span
                    className="transfer-disclaimer-modal__countdown-inline"
                    dir="ltr"
                  >
                    <UIText
                      text={effectiveTimerDisplay}
                      tag={TEXT_TAGS.SPAN}
                      size={18}
                      weight={700}
                      style="transfer-disclaimer-modal__countdown-value"
                    />
                  </span>
                </div>
              ) : row.key === "balanceAvailable" ? (
                <span className="transfer-disclaimer-modal__balance-row">
                  <UIText
                    text={t(`transferDisclaimer.${row.key}`, interpolations)}
                    tag={TEXT_TAGS.SPAN}
                    size={18}
                    weight={400}
                  />
                  <span
                    ref={infoRef}
                    className="transfer-disclaimer-modal__info"
                  >
                    <button
                      type="button"
                      className="transfer-disclaimer-modal__info-btn"
                      aria-expanded={tooltipOpen}
                      aria-label={t(
                        "hero.flightLockCountdownBanner.availableAmountInfoTitle",
                      )}
                      onClick={() => setTooltipOpen((open) => !open)}
                    >
                      <img
                        src={INFO_ICON_SRC}
                        alt=""
                        className="transfer-disclaimer-modal__info-icon"
                        width={16}
                        height={16}
                      />
                    </button>
                    {tooltipOpen && (
                      <div
                        className="transfer-disclaimer-modal__tooltip"
                        role="tooltip"
                      >
                        <UIText
                          text={t(
                            "hero.flightLockCountdownBanner.availableAmountInfoTitle",
                          )}
                          tag={TEXT_TAGS.P}
                          size={16}
                          weight={500}
                          style="transfer-disclaimer-modal__tooltip-title"
                        />
                        <div
                          className="transfer-disclaimer-modal__tooltip-divider"
                          aria-hidden
                        />
                        <p className="transfer-disclaimer-modal__tooltip-body">
                          {t(
                            "hero.flightLockCountdownBanner.availableAmountInfoBody1",
                          )}
                          <a
                            href={TERMS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transfer-disclaimer-modal__tooltip-link"
                          >
                            {t(
                              "hero.flightLockCountdownBanner.availableAmountInfoTermsLink",
                            )}
                          </a>
                          {t(
                            "hero.flightLockCountdownBanner.availableAmountInfoBody2",
                          )}
                          <a
                            href={USAGE_LIMITS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transfer-disclaimer-modal__tooltip-link"
                          >
                            {t(
                              "hero.flightLockCountdownBanner.availableAmountInfoUsageLimitsLink",
                            )}
                          </a>
                          {t(
                            "hero.flightLockCountdownBanner.availableAmountInfoBody3",
                          )}
                        </p>
                      </div>
                    )}
                  </span>
                </span>
              ) : (
                <UIText
                  text={t(`transferDisclaimer.${row.key}`, interpolations)}
                  tag={TEXT_TAGS.SPAN}
                  size={18}
                  weight={400}
                />
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="transfer-disclaimer-modal__disclaimer-panel">
        <Checkbox
          checked={thirdPartyConsent}
          onChange={setThirdPartyConsent}
          size={16}
          label={t(
            "transferDisclaimer.thirdPartyTransferConsentLabel",
            interpolations,
          )}
          labelFontSize={14}
        />
      </div>

      <div className="transfer-disclaimer-modal__actions">
        <Button
          onClick={handleConfirm}
          variant="primary"
          disabled={!thirdPartyConsent || isTransferring}
        >
          {t("transferDisclaimer.confirmButton")}
        </Button>
      </div>

      {isTransferring &&
        createPortal(
          <div
            className="transfer-disclaimer-modal__loader"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <div
              className="transfer-disclaimer-modal__loader-backdrop"
              aria-hidden
            />
            <div className="transfer-disclaimer-modal__loader-content">
              {animationData ? (
                <Lottie
                  animationData={animationData}
                  loop
                  autoplay
                  style={{ width: 200, height: 200 }}
                />
              ) : null}
              <UIText
                text={t("transferDisclaimer.transferLoadingMessage")}
                tag={TEXT_TAGS.P}
                size={32}
                weight={500}
                style="transfer-disclaimer-modal__loader-message"
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default TransferDisclaimerModal;
